import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API, useAuth } from '../contexts/AuthContext';

// Painel de avisos da diretoria.
//
// As REGRAS de cada aviso (o que consultar, quais limites) moram no código do
// backend — decisão tomada com o diretor: cada aviso tem regra própria, elas
// mudam pouco, e SQL vindo de tela rodaria com o usuário da aplicação, que tem
// permissão de escrita no banco de produção.
//
// Por aqui se controla só o dia a dia: ligar/desligar, o canal do WhatsApp e
// quem recebe.

const GRAVIDADE = {
  alta:  { rotulo: 'Alta',  cls: 'bg-danger' },
  media: { rotulo: 'Média', cls: 'bg-warning text-dark' },
  baixa: { rotulo: 'Baixa', cls: 'bg-secondary' },
};

function formatarData(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function Avisos() {
  const { temPermissao } = useAuth();
  const podeEditar = temPermissao('diretoria:avisos');

  const [regras, setRegras] = useState([]);
  const [ocorrencias, setOcorrencias] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [testando, setTestando] = useState(null);

  const [modalRegra, setModalRegra] = useState(null);
  const [inscritos, setInscritos] = useState([]);
  const [disponiveis, setDisponiveis] = useState([]);
  const [busca, setBusca] = useState('');
  const [carregandoModal, setCarregandoModal] = useState(false);
  const [novoNumero, setNovoNumero] = useState('');
  const [novoNome, setNovoNome] = useState('');
  const [salvandoNumero, setSalvandoNumero] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const [r, o] = await Promise.all([
        axios.get(`${API}/avisos/regras`),
        axios.get(`${API}/avisos/ocorrencias?limite=50`),
      ]);
      setRegras(r.data?.data || []);
      setOcorrencias(o.data?.data || []);
      setErro('');
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível carregar os avisos.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const salvarRegra = async (regra, campos) => {
    try {
      await axios.patch(`${API}/avisos/regras/${regra.id}`, campos);
      await carregar();
    } catch (e) {
      Swal.fire('Não deu', e.response?.data?.message || 'Falha ao salvar.', 'error');
    }
  };

  const testar = async (regra) => {
    setTestando(regra.id);
    try {
      const { data } = await axios.post(`${API}/avisos/regras/${regra.id}/executar`);
      const r = data?.data || {};
      const linhas = [
        `Encontrou: ${r.linhas ?? 0} registro(s)`,
        `Novos (nunca avisados): ${r.novas ?? 0}`,
        r.mensagens ? `Mensagens enviadas: ${r.mensagens} para ${r.inscritos} pessoa(s)` : null,
        r.pulou ? `Não enviou: ${r.pulou}` : null,
        r.erro ? `Erro: ${r.erro}` : null,
      ].filter(Boolean);
      Swal.fire({
        icon: r.erro ? 'error' : 'success',
        title: regra.nome,
        html: linhas.join('<br>'),
      });
      await carregar();
    } catch (e) {
      Swal.fire('Não deu', e.response?.data?.message || 'Falha ao executar.', 'error');
    } finally {
      setTestando(null);
    }
  };

  const abrirInscritos = async (regra) => {
    setModalRegra(regra);
    setBusca('');
    setCarregandoModal(true);
    try {
      const [i, d] = await Promise.all([
        axios.get(`${API}/avisos/regras/${regra.id}/inscritos`),
        axios.get(`${API}/avisos/regras/${regra.id}/contatos-disponiveis`),
      ]);
      setInscritos(i.data?.data || []);
      setDisponiveis(d.data?.data || []);
    } finally {
      setCarregandoModal(false);
    }
  };

  const buscarContatos = async (termo) => {
    setBusca(termo);
    if (!modalRegra) return;
    const { data } = await axios.get(
      `${API}/avisos/regras/${modalRegra.id}/contatos-disponiveis`,
      { params: { busca: termo || undefined } },
    );
    setDisponiveis(data?.data || []);
  };

  const inscrever = async (contatoId) => {
    const { data } = await axios.post(
      `${API}/avisos/regras/${modalRegra.id}/inscritos`, { contato_id: contatoId },
    );
    setInscritos(data?.data || []);
    await buscarContatos(busca);
    await carregar();
  };

  const inscreverNumero = async (e) => {
    e.preventDefault();
    setSalvandoNumero(true);
    try {
      const { data } = await axios.post(
        `${API}/avisos/regras/${modalRegra.id}/inscritos-por-numero`,
        { numero: novoNumero, nome: novoNome },
      );
      setInscritos(data?.data || []);
      setNovoNumero('');
      setNovoNome('');
      await carregar();
    } catch (err) {
      Swal.fire('Não deu', err.response?.data?.message || 'Falha ao adicionar.', 'error');
    } finally {
      setSalvandoNumero(false);
    }
  };

  const desinscrever = async (contatoId) => {
    const { data } = await axios.delete(
      `${API}/avisos/regras/${modalRegra.id}/inscritos/${contatoId}`,
    );
    setInscritos(data?.data || []);
    await buscarContatos(busca);
    await carregar();
  };

  if (carregando) {
    return <div className="p-4"><div className="spinner-border" role="status" /></div>;
  }

  return (
    <div className="container-fluid py-3">
      <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
        <div>
          <h4 className="mb-1">Avisos</h4>
          <p className="text-muted mb-0" style={{ maxWidth: '60ch' }}>
            Cada aviso vigia uma situação da operação e manda no WhatsApp quando ela
            acontece. Aqui você liga, desliga e escolhe quem recebe — a regra de cada
            um fica no código, porque muda pouco e cada uma é diferente.
          </p>
        </div>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={carregar}>
          <i className="bi bi-arrow-clockwise me-1" /> Atualizar
        </button>
      </div>

      {erro && <div className="alert alert-danger">{erro}</div>}

      {/* O canal e fixo: todo aviso sai pelo Martelinho. Nao ha o que escolher,
          entao vira uma linha de informacao e nao um campo por card. */}
      {!!regras.length && (
        <p className="text-muted small mb-3">
          <i className="bi bi-whatsapp me-1" />
          Tudo sai pelo <strong>Martelinho</strong>
          {regras[0].canal_numero ? ` (${regras[0].canal_numero})` : ''}
          {regras[0].canal_nome ? '' : ' — atenção: não está conectado agora'}
        </p>
      )}

      {!regras.length && (
        <div className="alert alert-info">
          Nenhum aviso cadastrado ainda. Peça para o TI criar o primeiro.
        </div>
      )}

      <div className="row g-3">
        {regras.map((r) => {
          const g = GRAVIDADE[r.gravidade] || GRAVIDADE.media;
          return (
            <div className="col-12 col-xl-6" key={r.id}>
              <div className={`card h-100 ${r.enabled ? '' : 'opacity-75'}`}>
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <h6 className="mb-1">
                        {r.nome} <span className={`badge ${g.cls} ms-1`}>{g.rotulo}</span>
                        {!r.presente_no_codigo && (
                          <span className="badge bg-dark ms-1">removido do código</span>
                        )}
                      </h6>
                      <p className="text-muted small mb-2">{r.descricao}</p>
                    </div>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={!!r.enabled}
                        disabled={!podeEditar || !r.presente_no_codigo}
                        onChange={(e) => salvarRegra(r, { enabled: e.target.checked })}
                        aria-label={`Ligar ${r.nome}`}
                      />
                    </div>
                  </div>

                  <div className="mb-2">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm w-100"
                      onClick={() => abrirInscritos(r)}
                    >
                      <i className="bi bi-people me-1" />
                      {r.total_inscritos} recebendo
                    </button>
                  </div>

                  {r.enabled && r.pendencias.length > 0 && (
                    <div className="alert alert-warning py-1 px-2 small mb-2">
                      Ligado, mas não vai enviar: {r.pendencias.join('; ')}.
                    </div>
                  )}

                  {r.ultimo_erro && (
                    <div className="alert alert-danger py-1 px-2 small mb-2">
                      Último erro: {r.ultimo_erro}
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-center small text-muted">
                    <span>
                      Roda a cada {r.intervalo_min} min · última vez {formatarData(r.ultima_execucao_em)}
                      {r.ultimas_linhas !== null && r.ultimas_linhas !== undefined
                        && ` · achou ${r.ultimas_linhas}`}
                    </span>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-nowrap ms-2"
                      disabled={testando === r.id || !r.presente_no_codigo}
                      onClick={() => testar(r)}
                    >
                      {testando === r.id ? 'rodando…' : 'rodar agora'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <h5 className="mt-4 mb-2">O que já foi detectado</h5>
      {!ocorrencias.length ? (
        <p className="text-muted">Nada ainda.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th>Quando</th><th>Aviso</th><th>Ocorrência</th><th>Enviado</th>
              </tr>
            </thead>
            <tbody>
              {ocorrencias.map((o) => (
                <tr key={o.id}>
                  <td className="text-nowrap small">{formatarData(o.detectado_em)}</td>
                  <td className="small">{o.regra_nome}</td>
                  <td className="small">{o.titulo}</td>
                  <td className="small">
                    {o.enviado_em
                      ? (
                        <>
                          {o.destinatarios} pessoa(s)
                          {o.resumida && <span className="badge bg-light text-dark ms-1">no resumo</span>}
                        </>
                      )
                      : <span className="text-muted">não enviado</span>}
                    {o.erro_envio && <div className="text-danger">{o.erro_envio}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalRegra && (
        <>
          <div className="modal show d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Quem recebe — {modalRegra.nome}</h5>
                  <button type="button" className="btn-close" onClick={() => setModalRegra(null)} />
                </div>
                <div className="modal-body">
                  {carregandoModal ? <div className="spinner-border" /> : (
                    <>
                      <h6>Recebendo agora</h6>
                      {!inscritos.length ? (
                        <p className="text-muted small">
                          Ninguém ainda — enquanto estiver assim, o aviso não envia nada.
                        </p>
                      ) : (
                        <ul className="list-group mb-3">
                          {inscritos.map((c) => (
                            <li key={c.id} className="list-group-item d-flex justify-content-between align-items-center py-1">
                              <span className="small">
                                {c.push_name || 'sem nome'} <span className="text-muted">{c.numero}</span>
                              </span>
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                disabled={!podeEditar}
                                onClick={() => desinscrever(c.id)}
                              >
                                remover
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}

                      <h6>Adicionar um número novo</h6>
                      <form className="row g-2 mb-3" onSubmit={inscreverNumero}>
                        <div className="col-sm-5">
                          <input
                            className="form-control form-control-sm"
                            placeholder="Número com DDD"
                            value={novoNumero}
                            onChange={(e) => setNovoNumero(e.target.value)}
                            required
                          />
                        </div>
                        <div className="col-sm-5">
                          <input
                            className="form-control form-control-sm"
                            placeholder="Nome de quem recebe"
                            value={novoNome}
                            onChange={(e) => setNovoNome(e.target.value)}
                            required
                          />
                        </div>
                        <div className="col-sm-2">
                          <button
                            type="submit"
                            className="btn btn-primary btn-sm w-100"
                            disabled={!podeEditar || salvandoNumero}
                          >
                            {salvandoNumero ? '...' : 'Incluir'}
                          </button>
                        </div>
                      </form>

                      <h6>Ou escolher um contato existente</h6>
                      <input
                        className="form-control form-control-sm mb-2"
                        placeholder="Buscar por nome ou número"
                        value={busca}
                        onChange={(e) => buscarContatos(e.target.value)}
                      />
                      {!disponiveis.length ? (
                        <p className="text-muted small">Nenhum contato encontrado.</p>
                      ) : (
                        <ul className="list-group">
                          {disponiveis.map((c) => (
                            <li key={c.id} className="list-group-item d-flex justify-content-between align-items-center py-1">
                              <span className="small">
                                {c.push_name || 'sem nome'} <span className="text-muted">{c.numero}</span>
                              </span>
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                disabled={!podeEditar}
                                onClick={() => inscrever(c.id)}
                              >
                                adicionar
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setModalRegra(null)}>
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" />
        </>
      )}
    </div>
  );
}
